/** A reseller's tenants (E12-T02, E12-T09): make one, set its limits, suspend and resume it. The
 * platform tenant (OEM mode) also makes resellers. */
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useId, useState, type FormEvent } from "react";
import { data, type Schemas } from "../api/client";
import { useBff, useMe } from "../app/session";
import { useT } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, ErrorState } from "../ui/states";
import { Table } from "../ui/Table";
import { LimitsForm, limitsSummary } from "./Limits";

type Child = Schemas["ChildTenantOut"];
const SLUG = /^[a-z0-9][a-z0-9-]{1,62}$/;
const FIELD = "w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm";

function useTenantsChanged() {
  const queries = useQueryClient();
  const tenant = useMe().tenant_id;
  return () => queries.invalidateQueries({ queryKey: ["tenants", tenant] });
}

export function Tenants({ tenants, platform }: { tenants: Child[]; platform: boolean }) {
  const t = useT();
  const [editing, setEditing] = useState<Child | null>(null);
  return (
    <div className="space-y-4">
      {tenants.length === 0 ? (
        <Empty title={t("tenants.empty")}><p>{t("tenants.emptyBody")}</p></Empty>
      ) : (
        <Table
          caption={t("tenants.title")}
          rows={tenants}
          rowKey={(c) => c.id}
          columns={[
            { header: t("tenants.col.name"), cell: (c) => (
              <span><span className="font-medium">{c.name}</span>{" "}
                <span className="font-mono text-xs text-[var(--color-muted)]">{c.slug}</span></span>
            ) },
            { header: t("tenants.col.kind"), cell: (c) => t(c.kind === "reseller" ? "tenants.kind.reseller" : "tenants.kind.standard") },
            { header: t("tenants.col.status"), cell: (c) => t(c.status === "suspended" ? "tenants.suspended" : "tenants.active") },
            { header: t("tenants.col.limits"), cell: (c) => limitsSummary(t, c.limits) },
            { header: t("tenants.col.actions"), cell: (c) => <Actions child={c} onLimits={() => setEditing(c)} /> },
          ]}
        />
      )}
      <NewTenant platform={platform} />
      <LimitsDialog child={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

function Actions({ child, onLimits }: { child: Child; onLimits: () => void }) {
  const t = useT();
  const { api } = useBff();
  const changed = useTenantsChanged();
  const suspended = child.status === "suspended";
  const toggle = useMutation({
    mutationFn: () => {
      const params = { params: { path: { tenant_id: child.id } } };
      return data(suspended ? api.POST("/v1/tenants/{tenant_id}/resume", params)
                            : api.POST("/v1/tenants/{tenant_id}/suspend", params)); // prettier-ignore
    },
    onSuccess: changed,
  });
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="secondary" onClick={onLimits} aria-label={t("tenants.limitsOf", { name: child.name })}>
        {t("tenants.limits")}
      </Button>
      <Button variant="ghost" disabled={toggle.isPending} onClick={() => toggle.mutate()}
        aria-label={t(suspended ? "tenants.resumeWho" : "tenants.suspendWho", { name: child.name })}>
        {t(suspended ? "tenants.resume" : "tenants.suspend")}
      </Button>
      {toggle.isError && <ErrorState error={toggle.error} />}
    </div>
  );
}

function LimitsDialog({ child, onClose }: { child: Child | null; onClose: () => void }) {
  const t = useT();
  const { api } = useBff();
  const changed = useTenantsChanged();
  const save = useMutation({
    mutationFn: (limits: Schemas["Limits"]) =>
      data(api.PUT("/v1/tenants/{tenant_id}/limits", { params: { path: { tenant_id: child?.id ?? "" } }, body: limits })),
    onSuccess: async () => {
      await changed();
      onClose();
    },
  });
  return (
    <Dialog.Root open={child !== null} onOpenChange={(open) => { if (!open) { save.reset(); onClose(); } }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-12 max-h-[85vh] w-full max-w-xl -translate-x-1/2 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-6 shadow-xl">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-lg font-semibold">{t("tenants.limitsOf", { name: child?.name ?? "" })}</Dialog.Title>
              <Dialog.Description className="text-sm text-[var(--color-muted)]">{t("tenants.limitsIntro")}</Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" aria-label={t("common.close")}><X size={16} aria-hidden="true" /></Button>
            </Dialog.Close>
          </div>
          {child && <LimitsForm initial={child.limits} busy={save.isPending} onSave={(l) => save.mutate(l)} />}
          {save.isError && <ErrorState error={save.error} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function NewTenant({ platform }: { platform: boolean }) {
  const t = useT();
  const id = useId();
  const { api } = useBff();
  const changed = useTenantsChanged();
  const [form, setForm] = useState({ name: "", slug: "", country: "", timezone: "", kind: "standard" });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  const create = useMutation({
    mutationFn: () =>
      data(api.POST("/v1/tenants", { body: {
        name: form.name.trim(), slug: form.slug.trim(), country: form.country.trim().toUpperCase(),
        timezone: form.timezone.trim(), kind: form.kind as "standard" | "reseller",
      } })), // prettier-ignore
    onSuccess: async () => {
      setForm({ name: "", slug: "", country: "", timezone: "", kind: "standard" });
      await changed();
    },
  });
  const slugProblem = form.slug && !SLUG.test(form.slug) ? t("tenants.slugRule") : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!slugProblem) create.mutate();
  };
  const field = (k: "name" | "slug" | "country" | "timezone", placeholder: string, extra: object = {}) => (
    <div className="space-y-1">
      <label htmlFor={`${id}-${k}`} className="block text-xs text-[var(--color-muted)]">{t(`tenants.field.${k}`)}</label>
      <input id={`${id}-${k}`} required value={form[k]} onChange={set(k)} placeholder={placeholder} className={FIELD}
        {...extra} />
    </div>
  );
  return (
    <form onSubmit={submit} className="max-w-2xl space-y-2" aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`} className="text-sm font-medium">{t("tenants.new")}</h3>
      <div className="grid gap-2 sm:grid-cols-2">
        {field("name", "Acme Clinics")}
        {field("slug", "acme-clinics", { "aria-invalid": slugProblem ? true : undefined })}
        {field("country", "IN", { maxLength: 2 })}
        {field("timezone", "Asia/Kolkata")}
        {platform && (
          <div className="space-y-1">
            <label htmlFor={`${id}-kind`} className="block text-xs text-[var(--color-muted)]">{t("tenants.col.kind")}</label>
            <select id={`${id}-kind`} value={form.kind} onChange={set("kind")} className={FIELD}>
              <option value="standard">{t("tenants.kind.standard")}</option>
              <option value="reseller">{t("tenants.kind.reseller")}</option>
            </select>
          </div>
        )}
      </div>
      {slugProblem && <p className="text-sm text-red-600">{slugProblem}</p>}
      <p className="text-xs text-[var(--color-muted)]">{t("tenants.newHint")}</p>
      <Button type="submit" disabled={create.isPending}>{t("tenants.create")}</Button>
      {create.isSuccess && <p role="status" className="text-sm">{t("tenants.created", { name: create.data.name })}</p>}
      {create.isError && <ErrorState error={create.error} />}
    </form>
  );
}
